<template>
	<v-form ref="detailsForm">
		<v-card :loading="loadingState">
			<v-card-text class="wrapper-div">
				<div class="wrapper-div">
					<v-expansion-panels v-model="openedDetailsPanel" elevation="0">
						<v-expansion-panel>
							<v-expansion-panel-title class="title-expansion">
								<h3>{{ $t('planning_details') }}</h3>
							</v-expansion-panel-title>

							<v-expansion-panel-text>
								<div class="pt-4 mt-3 px-4 bg-grey-lighten-3 rounded-lg">
									<div class="section-title mb-5">{{ $t('planning_basic') }}</div>

									<v-row>
										<v-col cols="6">
											<DropDownWithChips
												v-model="changedDuty.user"
												:label="$t('planning_driver')"
												:items="allDriversData || {}"
												label-key="displayName"
												search-placeholder="driver"
												disabled
											/>
										</v-col>

										<v-col cols="6">
											<v-select
												v-model="changedDuty.type"
												:items="typeList"
												item-title="name"
												item-value="id"
												:label="$t('planning_type')"
												variant="underlined"
												density="compact"
												hide-details
												disabled
											></v-select>
										</v-col>
									</v-row>

									<v-row>
										<v-col cols="6">
											<v-text-field
												v-model="changedDuty.name"
												:label="$t('planning_duty_name')"
												variant="underlined"
												:rules="nameRules"
												:disabled="!requireDuty || isDisabled('name')"
											></v-text-field>
										</v-col>

										<v-col cols="6">
											<v-text-field
												v-model="changedDuty.root"
												:label="$t('planning_root_duty')"
												variant="underlined"
												disabled
											></v-text-field>
										</v-col>
									</v-row>

									<v-row>
										<v-col cols="6" class="mt-n6">
											<v-autocomplete
												v-model="changedDuty.qualifications"
												:items="qualifications"
												:label="$t('planning_required_qualifications')"
												:closable-chips="true"
												item-value="id"
												item-title="name"
												variant="underlined"
												chips
												multiple
												item-color="primary"
												:disabled="!requireDuty"
											>
												<template v-slot:divider="{ props }">
													<div class="d-flex ga-4 align-center">
														<v-divider></v-divider>
														{{ props.text }}
														<v-divider></v-divider>
													</div>
												</template>

												<template #prepend-item>
													<v-list-item
														:title="allQualificationsSelected ? $t('Unselect_all') : $t('action_select_all')"
														@click="toggleSelectAllQualifications"
													>
														<template #prepend>
															<v-checkbox-btn
																:model-value="allQualificationsSelected"
																:indeterminate="someQualificationsSelected && !allQualificationsSelected"
																color="primary"
															></v-checkbox-btn>
														</template>
													</v-list-item>
													<v-divider class="mb-2" />
												</template>
											</v-autocomplete>
										</v-col>
										<v-col>
											<v-text-field
												v-if="planningBridge.editor.superuser && isSpecific"
												:label="$t('planning_duty_id')"
												variant="underlined"
												class="mt-n1"
												disabled
												readonly
												v-model="getDutyId"
												density="compact"
											></v-text-field>
										</v-col>
									</v-row>
									<v-row>
										<v-col cols="6">
											<v-text-field
												v-if="planningBridge.editor.superuser && isSpecific"
												:label="$t('user_field_provider')"
												variant="underlined"
												disabled
												readonly
												v-model="rawDuty.provider"
												density="compact"
											></v-text-field>
										</v-col>
										<v-col>
											<v-text-field
												v-if="planningBridge.editor.superuser && isSpecific"
												:label="$t('user_field_providerId')"
												variant="underlined"
												disabled
												readonly
												v-model="rawDuty.providerId"
												density="compact"
											></v-text-field>
										</v-col>
									</v-row>
									<v-row v-if="rawDuty?.preview && rawDuty.original && planningBridge.editor.superuser">
										<v-col cols="6">
											<v-text-field
												:label="$t('preview_field_id')"
												variant="underlined"
												disabled
												readonly
												v-model="rawDuty.preview"
												density="compact"
											></v-text-field>
										</v-col>
										<v-col>
											<v-text-field
												:label="$t('original_field_id')"
												variant="underlined"
												disabled
												readonly
												v-model="rawDuty.original"
												density="compact"
											></v-text-field>
										</v-col>
									</v-row>
								</div>
							</v-expansion-panel-text>
						</v-expansion-panel>
					</v-expansion-panels>

					<div class="content-div mt-3">
						<div class="section-title pb-3">{{ $t('planning_time') }}</div>

						<v-row>
							<v-col cols="6">
								<v-text-field
									v-model="changedDuty.startStop"
									:disabled="!requireDuty || isDisabled('startStop')"
									variant="underlined"
									:label="$t('planning_start_stop')"
								></v-text-field>
							</v-col>

							<v-col cols="6">
								<TimeEntryForm
									v-model="changedDuty.start"
									:rules="timeRuleFactory('end')"
									:disabled="isTimeFieldDisabled('start')"
									:required="requireDutyOrAbsence && !isDisabled('start') && !!changedDuty.end"
									cols="6"
									label="planning_start_time"
									ref="startTime"
								></TimeEntryForm>
							</v-col>
						</v-row>

						<v-row>
							<v-col cols="6">
								<v-text-field
									v-model="changedDuty.endStop"
									:disabled="!requireDuty || isDisabled('endStop')"
									variant="underlined"
									:label="$t('planning_end_stop')"
								></v-text-field>
							</v-col>

							<v-col cols="6">
								<TimeEntryForm
									v-model="changedDuty.end"
									:rules="timeRuleFactory('start')"
									:disabled="isTimeFieldDisabled('end')"
									:required="requireDutyOrAbsence && !isDisabled('end') && !!changedDuty.start"
									cols="6"
									label="planning_end_time"
									ref="startTime"
								></TimeEntryForm>
							</v-col>
						</v-row>

						<v-row>
							<v-col cols="6">
								<TimeEntryForm
									v-model="changedDuty.standbyStart"
									cols="3"
									label="planning_standby_start_time"
									:rules="timeRuleFactory('standbyEnd')"
									:disabled="!requireDutyOrAbsence || isDisabled('standbyStart')"
									:required="requireDutyOrAbsence && !isDisabled('standbyStart') && !!changedDuty.standbyEnd"
									ref="standbyStartTime"
									data-testid="standbyStartTime"
								/>
							</v-col>

							<v-col cols="6">
								<TimeEntryForm
									v-model="changedDuty.standbyEnd"
									cols="3"
									label="planning_standby_end_time"
									:rules="timeRuleFactory('standbyStart')"
									:disabled="!requireDutyOrAbsence || isDisabled('standbyEnd')"
									:required="requireDutyOrAbsence && !isDisabled('standbyEnd') && !!changedDuty.standbyStart"
									ref="standbyEndTime"
									data-testid="standbyEndTime"
								/>
							</v-col>
						</v-row>

						<v-row>
							<v-col cols="6">
								<TimeEntryForm
									v-model="changedDuty.additionalWorkTime"
									label="duty_additional_workTime"
									:required="requireDuty"
									:disabled="!requireDuty"
									cols="4"
								></TimeEntryForm>
							</v-col>

							<v-col cols="6">
								<TimeEntryForm
									v-model="changedDuty.workTime"
									:required="requireDutyOrReserve && !isDisabled('workTime')"
									:disabled="!requireDutyOrReserve || isDisabled('workTime')"
									label="duty_workTime_calculated"
									readonly
								></TimeEntryForm>
							</v-col>
						</v-row>
						<v-row>
							<v-col cols="6">
								<time-entry-form
									v-model="workTimeDb"
									v-if="planningBridge.editor.superuser && requireDutyOrReserve"
									label="duty_workTime_effective"
									readonly
								></time-entry-form>
							</v-col>
						</v-row>
					</div>

					<div class="content-div">
						<div class="section-title mb-5">{{ $t('planning_labels') }}</div>
						<LabelManager v-model="changedDuty.labels" :edit="hasEditPermission" />
					</div>

					<div class="content-div">
						<div class="section-title mb-5">{{ $t('planning_comments') }}</div>
						<v-textarea
							v-model="changedDuty.notes"
							:label="$t('planning_information_for_employees')"
							:placeholder="$t('planning_type_here')"
							:disabled="!requireDuty"
						></v-textarea>
					</div>

					<div class="content-div">
						<div class="section-title mb-5">{{ $t('planning_breaks') }}</div>

						<v-row v-for="(item, index) in breaks" :key="index" class="align-center">
							<v-col cols="auto">
								<div class="text-caption">
									{{ $t('duty_break_paid') }}
								</div>

								<v-checkbox
									:model-value="breaks[index]?.paid"
									:disabled="isDisabled('breaks')"
									@update:model-value="(val) => handlePaidChange(index, val)"
									hide-details
									density="compact"
									class="mt-n1 mb-4"
									color="primary-green"
								/>
							</v-col>

							<v-col>
								<TimeEntryForm
									v-model="breaks[index].start"
									:max="breaks[index].end"
									label="planning_start_time"
									:rules="breakValidationRule('end', index)"
									@update:model-value="breakTimeChange(index)"
									@blur="validateBreakFields(index)"
									:disabled="isDisabled('breaks')"
									required
									:ref="'breakStart' + index"
								/>
							</v-col>

							<v-col>
								<TimeEntryForm
									v-model="breaks[index].end"
									:min="breaks[index].start"
									label="planning_end_time"
									:rules="breakValidationRule('start', index)"
									@update:model-value="breakTimeChange(index)"
									@blur="validateBreakFields(index)"
									:disabled="isDisabled('breaks')"
									required
									:ref="'breakEnd' + index"
								/>
							</v-col>

							<v-col>
								<v-text-field :model-value="breaks[index].duration" :label="$t('duty_duration')" readonly disabled variant="underlined" />
							</v-col>

							<v-col cols="auto" class="d-flex justify-center">
								<v-btn icon="mdi-close" variant="text" color="error" @click="removeBreak(index)" :disabled="isDisabled('breaks')" />
							</v-col>
						</v-row>

						<div class="d-flex align-center justify-left mt-3">
							<v-btn :disabled="!requireDuty && isDisabled('breaks')" color="primary-green" class="mr-2" @click="addNewBreak">
								{{ $t('planning_add_breaks') }}
							</v-btn>
						</div>
					</div>

					<v-expansion-panels v-if="isSpecific" v-model="openedExtraPayPanel" elevation="0">
						<v-expansion-panel>
							<v-expansion-panel-title class="title-expansion">
								<h3>{{ $t('setup.extraPay') }}</h3>
							</v-expansion-panel-title>

							<v-expansion-panel-text>
								<div class="py-4 mt-3 px-4 bg-grey-lighten-3 rounded-lg">
									<v-row v-if="planningStore.singleStatistics?.extraPayNight1 !== undefined">
										<v-col>
											<div>
												<div class="mb-2 text-grey-darken-1">{{ $t('planning_extraPayNight1') }}</div>
												<v-badge
													inline
													color="grey-lighten-2 text-grey-darken-1 px-3"
													:content="`${extraPayNight1} ${planningBridge.extraPayCurrency}`"
												/>
											</div>
										</v-col>

										<v-col>
											<div>
												<div class="mb-2 text-grey-darken-1">{{ $t('planning_extraPayNight2') }}</div>
												<v-badge
													inline
													color="grey-lighten-2 text-grey-darken-1 px-3"
													:content="`${extraPayNight2} ${planningBridge.extraPayCurrency}`"
												/>
											</div>
										</v-col>
									</v-row>

									<v-row>
										<v-col>
											<div>
												<div class="mb-2 text-grey-darken-1">{{ $t('planning_extraPaySaturday') }}</div>
												<v-badge
													inline
													color="grey-lighten-2 text-grey-darken-1 px-3"
													:content="`${extraPaySaturday} ${planningBridge.extraPayCurrency}`"
												/>
											</div>
										</v-col>

										<v-col>
											<div>
												<div class="mb-2 text-grey-darken-1">{{ $t('planning_extraPaySunday') }}</div>
												<v-badge
													inline
													color="grey-lighten-2 text-grey-darken-1 px-3"
													:content="`${extraPaySunday} ${planningBridge.extraPayCurrency}`"
												/>
											</div>
										</v-col>
									</v-row>
								</div>

								<div class="py-4 mt-3 px-4 bg-grey-lighten-3 rounded-lg">
									<v-row v-if="planningBridge.extraPayAccounts?.length" class="mt-4 mb-1">
										<v-col v-for="(account, index) in planningBridge.extraPayAccounts" :key="index" cols="6" class="mb-3">
											<v-text-field
												v-model="extraPayAccountQuantityLocal[account.number]"
												label-size="sm"
												:label="`${account.name} (${account.number})`"
												:rules="[(v) => !v || !isNaN(parseFloat(v)) || $t('planning_invalid_number')]"
												@blur="updateExtraPayAccountQuantity(account.number)"
												:disabled="!hasEditPermission && !canPartialEdit"
												density="compact"
												variant="underlined"
												hide-details="auto"
											></v-text-field>
										</v-col>
									</v-row>
								</div>
							</v-expansion-panel-text>
						</v-expansion-panel>
					</v-expansion-panels>

					<v-expansion-panels v-if="isSpecific" v-model="openedTimeAllowancePanel" elevation="0">
						<v-expansion-panel>
							<v-expansion-panel-title class="title-expansion">
								<h3>{{ $t('setup.timeAllowance') }}</h3>
							</v-expansion-panel-title>

							<v-expansion-panel-text>
								<div class="py-4 mt-3 px-4 bg-grey-lighten-3 rounded-lg">
									<div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px">
										<div v-if="planningStore.singleStatistics.timeAllowanceNight1 !== undefined">
											<div class="mb-2 text-grey-darken-1">{{ $t('planning_timeAllowanceNight1') }}</div>
											<v-badge
												inline
												color="grey-lighten-2 text-grey-darken-1 px-3"
												:content="formatSecondsDuration(planningStore.singleStatistics?.timeAllowanceNight1)"
											/>
										</div>

										<div v-if="planningStore.singleStatistics.timeAllowanceNight2a !== undefined">
											<div class="mb-2 text-grey-darken-1">{{ $t('planning_timeAllowanceNight2a') }}</div>
											<v-badge
												inline
												color="grey-lighten-2 text-grey-darken-1 px-3"
												:content="formatSecondsDuration(planningStore.singleStatistics?.timeAllowanceNight2a)"
											/>
										</div>

										<div v-if="planningStore.singleStatistics.timeAllowanceNight2b !== undefined">
											<div class="mb-2 text-grey-darken-1">{{ $t('planning_timeAllowanceNight2b') }}</div>
											<v-badge
												inline
												color="grey-lighten-2 text-grey-darken-1 px-3"
												:content="formatSecondsDuration(planningStore.singleStatistics?.timeAllowanceNight2b)"
											/>
										</div>

										<div>
											<div class="mb-2 text-grey-darken-1">{{ $t('planning_timeAllowanceBreak1') }}</div>
											<v-badge
												inline
												color="grey-lighten-2 text-grey-darken-1 px-3"
												:content="formatSecondsDuration(planningStore.singleStatistics.timeAllowanceBreak1)"
											/>
										</div>

										<div>
											<div class="mb-2 text-grey-darken-1">{{ $t('planning_timeAllowanceBreak2') }}</div>
											<v-badge
												inline
												color="grey-lighten-2 text-grey-darken-1 px-3"
												:content="formatSecondsDuration(planningStore.singleStatistics?.timeAllowanceBreak2)"
											/>
										</div>
									</div>
								</div>
							</v-expansion-panel-text>
						</v-expansion-panel>
					</v-expansion-panels>
				</div>
			</v-card-text>
			<div v-if="isAffectedByPreview" class="affected-duty-bar border-top flex-shrink-0 d-flex align-center">
				<v-icon icon="mdi-alert" color="warning" size="small" class="mr-3" />
				<div class="d-flex flex-column">
					<span class="text-body-2">
						{{ $t('duty_affected_by_preview', { names: editorsText }) }}
					</span>
				</div>
			</div>
			<div class="save-changes-div mb-5" v-if="hasChanges && !isActive">
				<div class="text-subtitle-2 mb-2 font-weight-bold d-flex align-center">
					<v-icon color="warning" class="mr-1" size="small">mdi-information</v-icon>
					<span>{{ t('button.pending_changes') }}</span>
				</div>
				<v-btn color="grey-lighten-1" variant="flat" class="mt-2 px-6 py-2 font-weight-bold h-100" @click="triggerUpdate(false)">
					{{ $t('action_apply') }}
				</v-btn>
			</div>
			<v-card-actions class="pa-4 bg-white">
				<v-btn v-if="hasChanges && (hasEditPermission || canPartialEdit)" variant="text" color="#000000DE" class="px-6 font-weight-bold" @click="close">
					{{ $t('action_cancel') }}
				</v-btn>
				<v-spacer> </v-spacer>
				<v-btn
					v-if="(hasEditPermission || canPartialEdit) && hasChanges && (!isPastOrPublishedDay || !isActive)"
					variant="flat"
					color="#F5F5F5"
					class="px-6 font-weight-bold"
					@click="triggerUpdate"
				>
					{{ $t('action_save') }}
				</v-btn>
				<v-btn
					v-if="(hasEditPermission || canPartialEdit) && isPastOrPublishedDay && hasChanges"
					variant="flat"
					color="teal-darken-4"
					:loading="previewButtonLoading"
					@click="() => handlePreviewButtonClick(() => triggerUpdate())"
				>
					<v-progress-circular v-if="previewButtonLoading" indeterminate size="18" width="2" />
					<span v-else>{{ $t(previewButtonLabelKey) }}</span>
				</v-btn>
				<v-btn v-if="(!hasEditPermission && !canPartialEdit) || !hasChanges" color="primary-green darken-1" variant="text" @click="close">
					{{ $t(!hasEditPermission && !canPartialEdit ? 'action_ok' : 'action_close') }}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-form>

	<AddCommentDialog
		v-if="showCommentDialog"
		:title="commentDialogTitle"
		:model-value="showCommentDialog"
		:action-type="'UPDATE'.toLowerCase()"
		@update="closeCommentDialog"
		@submit="handleSubmit"
	/>

	<PlanningRootDutiesModal
		v-if="rootDutyDialog"
		v-model="rootDutyDialog"
		:rootDuties="filterDuties"
		:selectedDuty="rawDuty"
		:day="duty?.day ?? rawDuty?.day"
		:changedDuty="changedFields"
		@cancel="handleRootDialogCancel"
		@submit="handleRootDuties"
	></PlanningRootDutiesModal>
</template>

<script setup lang="ts">
import { onBeforeMount, computed, ref } from 'vue';
import { useDutyForm } from './PlanningDialogDetailsTab/useDutyForm';
import { usePlanningBridgeStore } from '@/rennovation/planning-view/store/bridge/planningBridge';
import { useDutyChangePreview } from '@/rennovation/planning-view/composables/useDutyChangePreview';
import AddCommentDialog from './AddCommentDialog.vue';
import LabelManager from '../../../../../../components/common/LabelManager.vue';
import TimeEntryForm from '../../../../../../components/DateAndTime/TimeEntryForm/TimeEntryForm.vue';
import DropDownWithChips from '../../Controls/DropDownWithChips.vue';
import PlanningRootDutiesModal from './PlanningRootDutiesModal.vue';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { usePlanningDayStatus } from '@/rennovation/planning-view/composables/useDayStatus';
import { useDayStore } from '@/rennovation/planning-view/store/useDayStore';

const { t } = useI18n();

const props = withDefaults(
	defineProps<{
		dutyID: string;
		isSpecific?: boolean;
		fieldsToDisable;
	}>(),
	{
		isSpecific: true,
		fieldsToDisable: [],
	}
);
const dayStore = useDayStore();

const { isActive, previewButtonLoading, previewButtonLabelKey, handlePreviewButtonClick, getEditorNames } = useDutyChangePreview();

const emit = defineEmits(['update:modelValue', 'showSnackbar', 'close', 'handleTabChange', 'duty-updated']);
const selectedDutyStatus = ref(JSON.parse(localStorage.getItem('selectedDutyStatus') || 'null') || ['published']);
const filterDuties = computed(() => {
	const selectedDutyId = rawDuty.value?.id;

	return rootDuties.value
		.filter((duty: any) => {
			if (!duty.day) return false;
			if (duty.day.status === 'locked') return false;

			return duty._id !== selectedDutyId;
		})
		.sort((a: any, b: any) => {
			const timeA = a.day?.date ? DateTime.fromISO(a.day.date).toMillis() : 0;
			const timeB = b.day?.date ? DateTime.fromISO(b.day.date).toMillis() : 0;
			return timeB - timeA;
		});
});
const editors = computed(() => getEditorNames(duty.value?.affectedByPreview));
const editorsText = computed(() => editors.value.join(', '));

defineOptions({
	inheritAttrs: false,
});
const {
	duty,
	planningStore,
	loadingState,
	openedDetailsPanel,
	openedExtraPayPanel,
	openedTimeAllowancePanel,
	rootDuties,
	rootDutyDialog,
	extraPayAccountQuantityLocal,
	rawDuty,
	changedDuty,
	showCommentDialog,
	allDriversData,
	qualifications,
	typeList,
	requireDuty,
	hasChanges,
	changedFields,
	extraPayNight1,
	extraPayNight2,
	extraPaySaturday,
	extraPaySunday,
	commentDialogTitle,
	hasEditPermission,
	planningBridge,
	formatSecondsDuration,
	nameRules,
	breaks,
	requireDutyOrReserve,
	requireDutyOrAbsence,
	hasCurrentDayDuties,
	getDutyId,
	workTimeDb,
	canPartialEdit,
	allQualificationsSelected,
	toggleSelectAllQualifications,
	someQualificationsSelected,
	handlePaidChange,
	handleRootDialogCancel,
	timeRuleFactory,
	breakValidationRule,
	breakTimeChange,
	validateBreakFields,
	isDisabled,
	addNewBreak,
	removeBreak,
	updateExtraPayAccountQuantity,
	triggerUpdate,
	closeCommentDialog,
	handleRootDuties,
	handleSubmit,
	isTimeFieldDisabled,
	init,
	close,
	isAffectedByPreview,
	uiStore,
	detailsForm,
} = useDutyForm(props, emit);

const { isPastOrPublishedDay } = usePlanningDayStatus(duty, dayStore.daysRef);

onBeforeMount(async () => {
	uiStore.tabClicked = false;
	await init(props.dutyID);
});
</script>

<style scoped>
.title-expansion {
	border-top: 1px solid #0000001f;
	border-bottom: 1px solid #0000001f;
}

.section-title {
	font-family: Roboto;
	font-weight: 700;
	font-style: Bold;
	font-size: 18px;
	line-height: 24px;
	letter-spacing: 0.5px;
	vertical-align: middle;
	color: #757575;
}
.content-div {
	background-color: #eeeeee;
	padding: 20px;
	margin-left: 24px;
	margin-right: 24px;
	margin-bottom: 24px;
	border-radius: 8px;
}
.wrapper-div {
	height: 500px;
	max-height: 500px;
	overflow-y: scroll;
}

.save-changes-div {
	background-color: #fff6eb;
	padding: 16px;
}

.affected-duty-bar {
	background-color: #fff6eb;
	color: #8a3b00;
	padding: 12px 16px;
}
</style>
